/**
 * Service responsible for determining what data will be passed to autocomplete.
 * The logic determines which values will show as possible autocomplete values. For example, for a column containing
 * numbers we might only want to show any data which is also of number datatype.
 *
 * @author Michal Drewniak (michal@blaze.tech)
 */

import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import fastIsEqualEs6 from 'fast-deep-equal/es6';
import capitalize from 'lodash-es/capitalize';
import { combineLatest, first, map, Observable, of, shareReplay, Subscription } from 'rxjs';

import {
  ColumnDataType,
  ComparisonOperator,
  TagItemInfo,
  VariableInfo,
  WorkflowVariableDefinitionInfo,
} from '@blaze-shared/api-contracts-domain';
import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { findDisplayColumnKey } from '@blaze-shared/elements-domain';
import { Formula, FormulasFacade } from '@blaze-shared/formulas-data-access';
import { compareAsc, compareAscBy } from '@blaze-shared/platform-util';
import { UrlVariable } from '@blaze-shared/projects-domain';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import {
  BaseSchema,
  BlazeSchema,
  convertDatasetColumnsToColumnInfos,
  DatasetColumn,
  DatasetRow,
  DatasetSchema,
  DEFAULT_ID_COL,
  findColumn,
  getIdColumnName,
  hasBlazeTableFlag,
  isBlazeTableSchema,
  isDatasetLinkValue,
  isLinkedColumn,
  isNonObjectValueSchemaColumns,
  matchColumn,
} from '@blaze-shared/schema-domain';
import { deepClone, isNullishOrEmptyString } from '@blaze-shared/shared-util';
import {
  DatasetWorkflowNodeBase,
  findParentForEachNode,
  hasAncestorNodeOfType,
  isAssignNode,
  isDatasetNode,
  isGetNode,
  isMergeNode,
  isServerOnlyWorkflow,
  MAIN_INPUT_NAME,
  NODES_WHICH_OUTPUT_INPUT1,
  shouldWorkflowSubscribeToChanges,
  WorkflowNode,
} from '@blaze-shared/workflow-domain';

import * as fromIntellisense from './+state/intellisense.selectors';
import { IntellisenseState } from './+state/intellisense.state';
import { IntelliQueryService } from './intelli-query.service';
import { IntelliSenseService } from './intellisense.service';
import { ContextValue } from './models/intelli-autocomplete.models';
import { IntelliConfig, IntelliContext } from './models/intellisense';
import { MAX_VALUES_PER_OPERATOR_NAME } from './models/intellisense-configuration';

@Injectable({ providedIn: 'root' })
export class IntelliAutoConfigService {
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly formulasFacade = inject(FormulasFacade);
  private readonly iQueryService = inject(IntelliQueryService);
  private readonly iss = inject(IntelliSenseService);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store: Store<IntellisenseState> = inject(Store);

  // Cache of dataset requests used to prevent fetching same data for linked table values.
  private readonly changedDatasetSubs = new Map<string, Subscription>();
  private readonly datasetRowsRequests = new Map<string, Observable<DatasetRow[] | undefined>>();

  getAccountColumns(config: IntelliConfig | null): Observable<DatasetColumn[] | null> {
    const accountsSchema = config?.accountsSchema;
    const columns = (accountsSchema?.columns || []).filter((col) =>
      this.filterOutInternal(col, true),
    );
    if (config?.disableGlobalVars || !this.canHaveOtherValues(config)) {
      return of([]);
    }

    // No schema for Account found. Try to deduce it from the current VarAccount
    if (!columns.length) {
      const account = this.projectContextFacade.varAccount;
      if (account?.Name) {
        for (const accountColumn in account) {
          if (Object.hasOwn(account, accountColumn)) {
            const value: any = (account as any)[accountColumn];
            const dataType = capitalize(typeof value) as ColumnDataType;
            columns.push({
              name: accountColumn,
              columnType: dataType,
              dataType,
              isProfilePicture: false,
            });
          }
        }
      }
    }

    // Account columns should not be shown at all in followin cases:
    const shouldNotShowAccountColumns = !!config?.isBlazeTableFormulaColumn;
    // Only the "Current Account" (account id) column should be shown
    const shouldShowOnlyCurrentAccount = this.isLinkToCurrentAccount(config);
    const shouldNotShowCurrentAccount = !this.isLinkToCurrentAccount(config);

    let accountColumns: DatasetColumn[] = [];
    const idCol = getIdColumnName(accountsSchema) || DEFAULT_ID_COL;
    if (shouldShowOnlyCurrentAccount) {
      accountColumns = columns.filter((col) => col.name === idCol);
    } else if (shouldNotShowAccountColumns) {
      accountColumns = [];
    } else if (shouldNotShowCurrentAccount) {
      accountColumns = columns.filter((col) => col.name !== idCol);
    }

    return of(accountColumns);
  }

  getContext(): Observable<IntelliContext | undefined> {
    return this.store.select(fromIntellisense.selectContext);
  }

  getPageContext(config: IntelliConfig | null): DatasetColumn[] | null {
    if (
      !this.canHaveOtherValues(config) ||
      !config?.pageContextSchema ||
      config?.isBlazeTableFormulaColumn // BL-1691
    ) {
      return null;
    }

    return config.pageContextSchema.columns.filter((column) => this.filterOutInternal(column));
  }

  /**
   * Predefined autocomplete values contains a list of predefined complex formulas which are easier for the user
   * to use then having them type the full formula themselves.
   * @param config
   * @returns
   */
  getPredefinedAutocompleteValues(config: IntelliConfig | null): ContextValue[] {
    if (!config) {
      return [];
    }

    const values: ContextValue[] = [];
    if (config.workflowNode?.nodeType === 'If') {
      values.push({
        display: 'Incoming data has rows',
        id: crypto.randomUUID(),
        isAutoCompleted: true,
        isRowId: false,
        value: 'ROWCOUNT() > 0',
        type: 'Condition',
      });
      values.push({
        display: 'Incoming data has errors',
        id: crypto.randomUUID(),
        isAutoCompleted: true,
        isRowId: false,
        value: 'HASERRORS()',
        type: 'Condition',
      });
    }

    if (config.staticOptions) {
      values.push(
        ...config.staticOptions.map((option) => ({
          display: option.label,
          id: crypto.randomUUID(),
          isAutoCompleted: true,
          isRowId: false,
          value: option.value,
        })),
      );
    }

    return values;
  }

  getInputSchema(config: IntelliConfig | null): Observable<BlazeSchema | null> {
    const node = config?.workflowNode as DatasetWorkflowNodeBase;
    if (config?.inputSchema) {
      return of(config.inputSchema);
    }
    // If output schema is not specified, we assume that datasetId found on the workflow node is the output table.
    if (node?.datasetId && !config?.outputSchema) {
      return this.schemaService.get(node.datasetId).pipe(map((schema) => schema || null));
    }
    return of(null);
  }

  /**
   * Returns schema used by the given node (for example Update, Insert, Delete). This schema is necessary to properly
   * get fixed values that can be used for assignments in those nodes.
   */
  getNodeDatasetSchema(config: IntelliConfig | null): Observable<BlazeSchema | null> {
    if (!isDatasetNode(config?.workflowNode)) {
      return of(null);
    }
    const node = config?.workflowNode as DatasetWorkflowNodeBase;
    if (node?.datasetId) {
      return this.schemaService.get(node.datasetId).pipe(map((schema) => schema || null));
    }
    return of(null);
  }

  /**
   * @returns Column names which can be set for the dataset on which operation is performed.
   */
  getDatasetReferenceColumns(config: IntelliConfig | null): Observable<DatasetColumn[]> {
    const schema = config?.datasetSchema;
    if (
      !this.canHaveOtherValues(config) ||
      !schema ||
      config?.disableDatasetColumns ||
      this.isLinkToCurrentAccount(config) ||
      this.isLinkToCurrentUser(config) ||
      this.schemaService.getSameNamedInputSchema(schema, config?.namedInputsSchemas) ||
      isNonObjectValueSchemaColumns(schema.columns) // Syntetic (artificial schema) columns are handle via named inputs
    ) {
      return of([]);
    }

    const columns = schema?.columns || [];
    let validColumns = columns
      .filter((col) => this.filterOutSelectedColumn(col, config))
      .filter((col) => this.filterOutInternal(col, false))
      .filter((col) => this.filterOutUnsupportedOperatorType(col, config))
      .filter((col) => {
        const matchesLinkedColumn = isLinkedColumn(col)
          ? this.columnMatchesSelectedColumnLink(config, col)
          : true;
        return (
          col.name && // Column name exists
          matchesLinkedColumn
        );
      });
    validColumns = validColumns.sort((itemA, itemB) => compareAsc(itemA?.name, itemB?.name));
    return of(validColumns);
  }

  getFormulas(config: IntelliConfig | null): Formula[] {
    if (config?.disableFormulas) {
      return [];
    }
    return this.formulasFacade.formulas;
  }

  /**
   * Returns columns associated with named inputs.
   * The value of these will ultimately become {ColumnIdOrName}
   */
  getNamedInputsColumns(config: IntelliConfig | null): Record<string, DatasetColumn[]> | undefined {
    const namedInputSchemas = config?.namedInputsSchemas || {};
    const columns: Record<string, DatasetColumn[]> = {};

    if (!this.canHaveOtherValues(config) || !Object.keys(namedInputSchemas)?.length) {
      return;
    }

    for (const inputName in namedInputSchemas) {
      // input1 is set as another named input. In order not to duplicate the options we skip it.
      // schemas that are duplicates of inputSchema should also be omitted.
      // BL-6643: For workflows which react to webhooks we need to show both input options. This is because when
      // the webhook refreshes the workflow, named input doesn't necessarily have the latest data. Instead, it's the
      // $$input1 which will have it.
      if (inputName === MAIN_INPUT_NAME && !shouldWorkflowSubscribeToChanges(config?.workflow)) {
        continue;
      }
      columns[inputName] = [];
      const namedInputSchema = namedInputSchemas[inputName];

      const isChangeTrackingRowIdColumn =
        isBlazeTableSchema(config?.datasetSchema) &&
        !!config.datasetSchema.isChangeTrackingTable &&
        config?.column?.name === 'RowId';

      const keepIdColumn: boolean =
        !!config?.column?.link || !!config?.column?.isId || isChangeTrackingRowIdColumn;
      const schemaColumns =
        namedInputSchema?.columns?.filter((col) => this.filterOutInternal(col, keepIdColumn)) || [];

      // For File or Object inputs, suggest whole input as a valid input and nest named input columns inside
      if (
        namedInputSchema?.inputName &&
        (config?.column?.dataType === 'File' || config?.column?.dataType === 'Object')
      ) {
        const objColumn: DatasetColumn = {
          columnType: 'Object',
          dataType: 'Object',
          isProfilePicture: false,
          name: '',
          dataTypeDef: {
            name: namedInputSchema.inputName,
            columns: convertDatasetColumnsToColumnInfos(namedInputSchema?.columns || []),
          },
        };
        columns[inputName] = [objColumn];
        continue;
      }

      // If the config we're confuguring is a link to the given named input table, add the ID column of the linked table
      // to the list of available columns.
      if (this.isColumnLinkedToTable(config?.column, namedInputSchema)) {
        const idColumn = namedInputSchema?.columns.find(({ name }) => name === DEFAULT_ID_COL);
        // Ensure we're not adding a duplicate ID column to the list.
        const isIdColumnInSchemaColumns = schemaColumns.find(({ id }) => id === idColumn?.id);
        if (idColumn && !isIdColumnInSchemaColumns) {
          columns[inputName].push(idColumn);
        }
      }

      columns[inputName] = columns[inputName].concat(schemaColumns);
    }
    return columns;
  }

  /**
   * Gets a list of columns which can be used inside a ForEach node with a prefix $$currentRow. The difference between
   * $$currentRow and $$input1 is that $$currentRow is one row and $$input1 is usually an array. $$currentRow columns
   * are immutable and depend solely on the input to the parent node.
   */
  getCurrentRowColumns(config: IntelliConfig | null): DatasetColumn[] {
    // In order to properly determine whether we're inside a ForEach node, we need both workflow and workflowNode.
    if (!config?.inputSchema || !config.workflow || !config.workflowNode) {
      return [];
    }
    // Check if the given node for which we're getting the colums is nested inside a ForEach node. If so, get the
    // columns from the inputSchema but ultimately assign them to $$currentRow object.
    const parentBlockNode = findParentForEachNode(config.workflowNode, config.workflow);
    if (!parentBlockNode) {
      return [];
    }
    // This schema contains $currentRow columns which are immutable.
    const currentRowSchema = config.nodeInputSchemasMap?.get(parentBlockNode.id);
    if (!currentRowSchema) {
      console.warn('Unable to determine input schema for node inside ForEach', { config });
      return [];
    }

    return this.getSchemaColumns({ ...config, inputSchema: currentRowSchema }, [], true);
  }

  /**
   * Returns a list of columns which are available only within the ForEach node scope (with the exception of Compute
   * which is available also outside). These columns' value will start with $$input1. For example, if a PrintToPdf
   * node executes inside the ForLoop, $$input1["pdfUrl"] will be available inside the ForEach node for further use.
   * @param config
   * @returns
   */
  getCurrentRowInnerColumns(config: IntelliConfig | null): DatasetColumn[] {
    // In order to properly determine whether we're inside a ForEach node, we need both workflow and workflowNode.
    if (!config?.inputSchema || !config.workflow || !config.workflowNode) {
      return [];
    }
    // Check if the given node for which we're getting the colums is nested inside a ForEach node. If so, get the
    // columns from the inputSchema but ultimately assign them to $$currentRow object.
    const parentBlockNode = findParentForEachNode(config.workflowNode, config.workflow);
    if (!parentBlockNode) {
      return [];
    }
    // Since ForEach nodes can be nested, the proper schema to use for the given workflow node should come from the
    // nodeInputSchemasMap.
    const nodeInputSchema = config.nodeInputSchemasMap?.get(config.workflowNode.id);
    if (!nodeInputSchema) {
      console.warn('Unable to determine input schema for node inside ForEach', { config });
      return [];
    }

    const currentRowSchema = config.nodeInputSchemasMap?.get(parentBlockNode.id);
    const currentRowColKeys = new Set(
      (currentRowSchema?.columns || []).map((col) => col.id ?? col.name),
    );
    const schemaColumns = this.getSchemaColumns(
      { ...config, inputSchema: nodeInputSchema },
      [],
      true,
    );

    // Filter out currentRowSchema columns from the fetched schema columns. Make sure to include "Add Formula Field"
    // columns which might have been crated inside the foreach node.
    const columns: DatasetColumn[] = [];
    for (const col of schemaColumns) {
      if (!currentRowColKeys.has(col.id ?? col.name)) {
        columns.push(col);
      }
    }
    return columns;
  }

  /**
   * @returns Column names from passed schema.
   * The value of these will ultimately become $$input1["<columnIdOrName>"]
   */
  getSchemaColumns(
    config: IntelliConfig | null,
    existingColumns: DatasetColumn[],
    getForCurrentRowInput = false,
  ): DatasetColumn[] {
    // Formula fields in BlazeTable should never suggest $$input1 values.
    if (config?.isBlazeTableFormulaColumn) {
      return [];
    }

    const schema = config?.inputSchema;
    const colDatasetId = schema?.id || '';
    const namedInputsSchemaColumnIds = this.getNamedInputSchemasColumnIds(
      config?.namedInputsSchemas,
    );

    const sameNamedInputSchema = this.schemaService.getSameNamedInputSchema(
      schema,
      config?.namedInputsSchemas,
    );

    let hasAncestorNodeWhichOutputsInput1 = false;
    if (config?.workflow && config.workflowNode) {
      hasAncestorNodeWhichOutputsInput1 = hasAncestorNodeOfType(
        config?.workflowNode,
        config?.workflow,
        NODES_WHICH_OUTPUT_INPUT1,
      );
    }

    // In some cases (for example actions), named schema (selectedRow) has the same name as input schema but different
    // columns. Input schema can have additional columns due to for example Compute node insde the action.
    // In such cases, we don't want to totally ignore schema columns. Instead, we check which columns are present
    // in named schema and return only the non-duplicate columns.
    // If the given node has an ancestor node which changes the input1, we return duplicate columns (so that user
    // has a choice to use either named input or changed input1).
    if (
      !hasAncestorNodeWhichOutputsInput1 &&
      sameNamedInputSchema &&
      !fastIsEqualEs6(schema, sameNamedInputSchema)
    ) {
      const namedInputsColumnNames = new Set<string>();
      for (const column of sameNamedInputSchema?.columns || []) {
        namedInputsColumnNames.add(column.name);
      }
      let columns = schema?.columns || [];
      if (!shouldWorkflowSubscribeToChanges(config?.workflow)) {
        // For workflows which react to webhooks we need to show both input options.
        columns = columns
          .filter((col) =>
            this.filterOutExistingNamedInputColumnsByName(col, namedInputsColumnNames, true),
          )
          .filter((col) =>
            this.filterOutExistingNamedInputColumnsById(col, namedInputsSchemaColumnIds, true),
          );
      }
      return columns;
    }
    // Check whether any columns should be returned at all.
    else if (
      !schema ||
      (sameNamedInputSchema && !hasAncestorNodeWhichOutputsInput1) ||
      !this.canHaveOtherValues(config) ||
      // First node doesn't have access to $$input1, except Merge node which uses $$input1 for iteration
      // We make an exception for Triggered workflows since they have data source from outside of the workflow itself.
      (config.workflow?.workflowType !== 'Trigger' &&
        !isMergeNode(config.workflowNode) &&
        this.isFirstNode(config.workflowNode, config)) ||
      (!colDatasetId && !schema?.columns?.length) || // Either dataset ID or columns must be specified
      isNonObjectValueSchemaColumns(schema.columns) // Syntetic (artificial schema) columns are handle via named inputs
    ) {
      return [];
    }

    // When a given node is inside a ForEach node, we don't want to display $$input1 column suggestions. Instead,
    // we should only show $$currentRow suggestions calculated inside getCurrentRowColumns(). That particular function
    // uses getSchemaColumns so we check for the override getForCurrentRow to make sure we return data when necessary.
    if (
      !getForCurrentRowInput &&
      config?.workflow &&
      config.workflowNode &&
      !!findParentForEachNode(config.workflowNode, config.workflow)
    ) {
      return [];
    }

    const columns: DatasetColumn[] = schema?.columns || [];
    let validColumns = columns.filter((col) => this.filterOutSelectedColumn(col, config));
    validColumns = validColumns.filter((col) => this.filterOutInternal(col));

    if (!hasAncestorNodeWhichOutputsInput1) {
      validColumns = validColumns.filter((col) =>
        this.filterOutExistingColumns(col, existingColumns),
      );
    }

    if (!hasAncestorNodeWhichOutputsInput1 && !shouldWorkflowSubscribeToChanges(config?.workflow)) {
      // Filter out the same columns that are present in named inputs to avoid duplicates
      // For workflows which react to webhooks we need to show both input options.
      validColumns = validColumns.filter((col) =>
        this.filterOutExistingNamedInputColumnsById(col, namedInputsSchemaColumnIds, true),
      );
    }

    if (!isAssignNode(config?.workflowNode)) {
      validColumns = validColumns.filter((col) => {
        const matchesLinkedColumn = isLinkedColumn(col)
          ? this.columnMatchesSelectedColumnLink(config, col)
          : true;
        return (
          col.name && // Column name exists
          matchesLinkedColumn
        );
      });
    }

    validColumns = validColumns.sort(compareAscBy('name'));
    return validColumns;
  }

  configHasColumnType(config: IntelliConfig | null): boolean {
    return !!config?.column || !!config?.columnTypes?.length;
  }

  /**
   * Get possible Tag values for the column defined in the schema.
   * @param config
   */
  getTagValuesFromSchema(config: IntelliConfig | null): Observable<TagItemInfo[]> {
    const column = config?.column; // Selected column
    let schemaColumns = config?.inputSchema?.columns || [];

    // Add named schema columns to check for values.
    const namedSchemas = config?.namedInputsSchemas || {};
    for (const schemaName in namedSchemas) {
      const schema = namedSchemas[schemaName];
      schemaColumns = schemaColumns.concat(schema?.columns || []);
    }
    // Make sure to fetch tag values from the datasetSchema. This can happen for example in inserts when we need to
    // be able to show tags from dataset into which we're going to insert a value.
    schemaColumns = schemaColumns.concat(config?.datasetSchema?.columns || []);
    schemaColumns = schemaColumns.concat(config?.fixedValuesSchema?.columns || []);
    schemaColumns = schemaColumns.concat(config?.accountsSchema?.columns || []);
    schemaColumns = schemaColumns.concat(config?.usersSchema?.columns || []);

    if (
      !this.canHaveOtherValues(config) ||
      !this.configHasColumnType(config) ||
      !schemaColumns?.length ||
      // Tag values should only be visible for tags column and for formulas. If it's not a tags column and not
      // a formulas column, do not include them.
      // TODO(michal): once formulas are enabled in actions, this check has to move to autocomplete values
      //  logic as an appropriate filter.
      (!this.isTagsColumn(column) &&
        !config?.columnTypes?.includes('Tag') &&
        !this.supportsFormulas(config))
    ) {
      return of([]);
    }
    const schemaColumn = schemaColumns.find((col) => col.id === column?.id);

    // Not returning avaialable tags does not apply when creating a condition inside an IF node.
    if (!schemaColumn && (!config?.workflowNode || config?.workflowNode?.nodeType !== 'If')) {
      return of([]);
    }

    let values: TagItemInfo[] = [];
    // isInline indicates that values are read from another column. If that's the case, return only tag values for that
    // given column.
    if (this.supportsFormulas(config) && !column?.isInline) {
      // For formula column, find all possible tag values in the schema.
      const schemaTagColumns = schemaColumns.filter((col) => this.isTagsColumn(col));
      schemaTagColumns.forEach((schemaColumn) => {
        values = values.concat(schemaColumn.tagDefinition?.items || []);
      });
    } else {
      // If it's not a formula column, use tag definitions from the schema column.
      values = schemaColumn?.tagDefinition?.items || [];
    }

    values = Array.from(new Set(values)); // Remove duplicates
    return of(values);
  }

  /**
   * Variables defined for the given page and read from the URL.
   */
  getUrlVars(config: IntelliConfig | null, context?: IntelliContext): UrlVariable[] {
    if (!config || !context?.urlVariables?.length || isServerOnlyWorkflow(config?.workflow)) {
      return [];
    }
    return context.urlVariables;
  }

  /**
   * @returns Returns a list of columns found in the system table Users and/or UserInfo stored in localStorage.
   * It also creates and adds a few helper columns such as "IsAdmin" or "IsInternal".
   */
  getUserColumns(
    config: IntelliConfig | null,
    context?: IntelliContext,
  ): Observable<DatasetColumn[] | null> {
    const usersSchema = config?.usersSchema;
    const columns = deepClone(usersSchema?.columns || []).filter((col) =>
      this.filterOutInternal(col, true),
    );

    if (
      !!config?.disableGlobalVars ||
      !this.canHaveOtherValues(config) ||
      // If it's a Blaze Table formula column, $$User should not be allowed to be used.
      !!config?.isBlazeTableFormulaColumn
    ) {
      return of([]);
    }

    // No schema for User found. Try to deduce it from the VarUser current VarUser.
    if (!columns.length) {
      const user = this.projectContextFacade.varUser;
      if (user) {
        for (const userColumn in user) {
          if (Object.prototype.hasOwnProperty.call(user, userColumn)) {
            const value: any = (user as any)[userColumn];
            const dataType = capitalize(typeof value) as ColumnDataType;
            columns.push({
              columnType: dataType,
              dataType,
              isProfilePicture: false, // isProfilePicture is never part of VarUser
              name: userColumn,
            });
          }
        }
      }
    }

    const userColumnNames = Object.keys(context?.user || {});
    const existingColumns = new Set(columns.map((col) => col.name));
    // Add User params from context that are not present in the Users table.
    if (userColumnNames.includes('IsAdmin') && !existingColumns.has('Name')) {
      columns.push({
        name: 'IsAdmin',
        columnType: 'Boolean',
        dataType: 'Boolean',
        isProfilePicture: false,
      });
    }
    if (userColumnNames.includes('IsInternal') && !existingColumns.has('Name')) {
      columns.push({
        name: 'IsInternal',
        columnType: 'Boolean',
        dataType: 'Boolean',
        isProfilePicture: false,
      });
    }
    if (userColumnNames.includes('Name') && !existingColumns.has('Name')) {
      columns.push({
        name: 'Name',
        columnType: 'String',
        dataType: 'String',
        isProfilePicture: false,
      });
    }
    if (userColumnNames.includes('ProfilePicture') && !existingColumns.has('ProfilePicture')) {
      columns.push({
        name: 'ProfilePicture',
        columnType: 'Image',
        dataType: 'Image',
        isProfilePicture: true,
      });
    }
    if (userColumnNames.includes('Timezone') && !existingColumns.has('Timezone')) {
      columns.push({
        name: 'Timezone',
        columnType: 'String',
        dataType: 'String',
        isProfilePicture: false,
      });
    }

    if (!columns?.length) {
      return of([]);
    }

    // Only the "Current User" (user id) column should be shown
    const shouldShowOnlyCurrentUser = this.isLinkToCurrentUser(config);
    const shouldNotShowCurrentUser = !this.isLinkToCurrentUser(config);

    let userColumns: DatasetColumn[] = [];
    const idCol = getIdColumnName(usersSchema) || DEFAULT_ID_COL;
    if (shouldShowOnlyCurrentUser) {
      userColumns = columns.filter((col) => col.name === idCol);
    } else if (shouldNotShowCurrentUser) {
      userColumns = columns.filter((col) => col.name !== idCol);
    }

    return of(userColumns);
  }

  /**
   * @returns Unique values found in the specified column on which operation is performed.
   */

  getValuesFromColumn(config: IntelliConfig | null): Observable<any[]> {
    const column = config?.column;

    if (
      !this.canHaveOtherValues(config) ||
      this.isLinkToCurrentAccount(config) ||
      this.isLinkToCurrentUser(config) ||
      !column
    ) {
      return of([]);
    }

    const linkedDatasetId = column?.link?.tableId;
    const valueRequests: {
      linkedDatasetId?: Observable<string>;
      linkedRows?: Observable<DatasetRow[]>;
      linkedSchema?: Observable<DatasetSchema | undefined>;
    } = {};

    // Boolean column with either no operator (actions) or operator set to "equals to" or "is not equal to"
    if (
      config.column?.dataType === 'Boolean' &&
      (!config?.operator ||
        config.operator === ComparisonOperator.Eq ||
        config.operator === ComparisonOperator.Neq)
    ) {
      return of(['true', 'false']);
    }
    // Linked field values
    if (linkedDatasetId && !this.isTextOperator(config.operator)) {
      valueRequests['linkedDatasetId'] = of(linkedDatasetId);
      valueRequests['linkedRows'] = this.getDatasetRows(linkedDatasetId);
      valueRequests['linkedSchema'] = this.schemaService.get(linkedDatasetId);
    } else {
      return of([]);
    }

    return combineLatest(valueRequests).pipe(
      first(),
      map(({ linkedDatasetId, linkedRows, linkedSchema }) => {
        this.clearRowsRequestsCacheOnDatasetChange(linkedDatasetId);

        let columnData: any[] = [];
        let columnIdOrName = '';
        if (linkedDatasetId && linkedSchema && linkedRows?.length) {
          const idCol = getIdColumnName(linkedSchema) || '';
          columnIdOrName = hasBlazeTableFlag(linkedSchema)
            ? findDisplayColumnKey(linkedSchema)
            : column.link?.displayColumn || '';
          const colName = this.schemaService.replaceColumnIdsWithNames(columnIdOrName);
          columnData =
            linkedRows
              .filter((row) => !!row?.[colName])
              .map((row) => {
                const isLinked = isDatasetLinkValue(row[colName]);
                const display = isLinked
                  ? row[colName].display || !isNullishOrEmptyString(row[colName].rowId)
                  : row[colName];
                const rowId = isLinked ? row[colName].rowId : row[idCol];
                const data = { rowId, display };
                return JSON.stringify(data);
              }) || [];
        } else {
          return [];
        }

        const uniqueSet = new Set(columnData);

        const uniqueValues: any[] = Array.from(uniqueSet).map((jsonValue) => JSON.parse(jsonValue));

        return uniqueValues.sort((itemA, itemB) => {
          const itemAKey: string = itemA?.toLowerCase ? itemA.toLowerCase() : '';
          const itemBKey: string = itemB?.toLowerCase ? itemB.toLowerCase() : '';
          return compareAsc(itemAKey, itemBKey);
        });
      }),
    );
  }

  getVars(
    config: IntelliConfig | null,
    context: IntelliContext | undefined,
  ): Partial<WorkflowVariableDefinitionInfo>[] {
    let varsFromWorkflow: Partial<VariableInfo>[] = config?.varsFromWorkflow || [];
    if (config?.workflow) {
      varsFromWorkflow = this.iss.getVariablesForWorkflow(config.workflow);
    }
    if (
      !this.canHaveOtherValues(config) ||
      this.isLinkToCurrentAccount(config) ||
      this.isLinkToCurrentUser(config) ||
      !!config?.isBlazeTableFormulaColumn ||
      (!context && !varsFromWorkflow?.length)
    ) {
      return [];
    }

    const vars: Partial<WorkflowVariableDefinitionInfo>[] = [];
    const varDefs = context?.vars || [];

    const parsedVarDefs = new Set<string>();
    for (const varDef of varDefs) {
      if (varDef.name && !parsedVarDefs.has(varDef.name)) {
        parsedVarDefs.add(varDef.name);
        vars.push({ ...varDef });
      }
    }
    for (const varFromWf of varsFromWorkflow) {
      if (varFromWf.name && !parsedVarDefs.has(varFromWf.name)) {
        vars.push({ ...varFromWf });
        parsedVarDefs.add(varFromWf.name);
      }
    }

    return vars;
  }

  /**
   * Determines whether provided token represents table column name.
   */
  isColumnNameValue(value?: string | null): boolean {
    if (!value) {
      return false;
    }
    value = `${value}`;

    const isLikelyJsonObject = value.trim().startsWith('{') && value.trim().endsWith('}');
    if (isLikelyJsonObject) {
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed === 'object') {
          // If the value is parsable, it represents serialized object and not column name.
          return false;
        }
      } catch {
        // Fail silently
      }
    }
    if (value?.match) {
      const matches = value?.match(/{([a-z]*?)}/gi); // find {ColumnName}
      return !!matches?.length;
    } else {
      return false;
    }
  }

  /**
   * Based on the current value determines whether any other values can be selected. For example, if it's a non-formula
   * column and user has already selected a column name as part of the value, no other values are allowed.
   */
  protected canHaveOtherValues(config: IntelliConfig | null): boolean {
    if (config?.newIntelli) {
      return true;
    }
    const isFormula =
      !!config?.isFormula ||
      this.supportsFormulas(config) ||
      config?.valueInfo?.displayInfos?.some(({ formulaName }) => !!formulaName);

    if (
      // Number of values exceeded for the given operator
      this.hasMoreValuesThanOperatorAllows(config) ||
      // For non-formulas, only one column name value (ie. { MyColumn }) is allowed
      (!isFormula && this.isColumnNameValue(config?.valueInfo?.value))
    ) {
      return false;
    }
    return true;
  }

  private clearRowsRequestsCacheOnDatasetChange(observedDatasetId?: string): void {
    if (!observedDatasetId || this.changedDatasetSubs.has(observedDatasetId)) {
      return;
    }
    // Reset cache when dataset for which we fetched rows has changed.
    const sub = this.datasetRowsService.observeDatasetChanged(observedDatasetId).subscribe(() => {
      this.datasetRowsRequests.delete(observedDatasetId);
      this.changedDatasetSubs.get(observedDatasetId)?.unsubscribe();
      this.changedDatasetSubs.delete(observedDatasetId);
    });
    this.changedDatasetSubs.set(observedDatasetId, sub);
  }

  private hasMoreValuesThanOperatorAllows(config: IntelliConfig | null): boolean {
    if (!config?.valueInfo?.displayInfos?.length || !config?.operator) {
      return false;
    }
    const operatorName = this.iQueryService.getComparisonOperatorName(
      config.operator,
      config.column,
    );
    const maxValues = MAX_VALUES_PER_OPERATOR_NAME[operatorName];
    if (maxValues !== undefined && maxValues >= 0) {
      let allAutocompleted = true;
      allAutocompleted = (config?.valueInfo?.displayInfos || [])
        .map(({ isAutoCompleted }) => !!isAutoCompleted)
        .reduce((allAutocompleted, curr) => allAutocompleted && curr);
      // If not all values are autocompleted, we must assume that user is typing in current value. In such a case,
      // we can only determine that maximum number of values has been reached only after the number of tokens
      // exceeds max number of values.
      return allAutocompleted
        ? config?.valueInfo?.displayInfos?.length >= maxValues
        : config?.valueInfo?.displayInfos?.length > maxValues;
    }
    return false;
  }

  private columnMatchesSelectedColumnLink(
    config: IntelliConfig | null,
    col: DatasetColumn,
  ): boolean {
    const selectedColumn = config?.column;
    if (
      // Lack of selected column might mean that this might be a value inside a formula. Default to showing.
      !selectedColumn ||
      !col ||
      // If a selectedColumn or checked column is not a link, we assume it matches selected column link.
      !isLinkedColumn(selectedColumn)
    ) {
      return true;
    }
    const tableIdColLinksTo = col?.link?.tableId || col.dataTypeDef?.columns[0]?.link?.tableId;
    return !!selectedColumn?.link?.tableId && selectedColumn?.link?.tableId === tableIdColLinksTo;
  }

  private filterOutExistingColumns(
    column: DatasetColumn,
    datasetColumns: DatasetColumn[],
  ): boolean {
    return !findColumn(datasetColumns, column);
  }

  /**
   * Compares specified col with a set of existing column IDs to determine whether that column should be kept (true)
   * or filtered out (false).
   * @param col - Column which we check whether it exists in the list of existing columns
   * @param columnIds - A set of IDs of existing columns
   * @param keepRowIdColumns - Whether Row ID (.isId) column should be kept even when found in existing IDs
   * @returns Whether specified col should be kept.
   */
  private filterOutExistingNamedInputColumnsById(
    col: DatasetColumn,
    columnIds: Set<string>,
    keepRowIdColumns: boolean,
  ): boolean {
    if (!columnIds.size) {
      return true;
    }
    if (keepRowIdColumns) {
      // BL-8201 We keep ID columns in case previous node was an insert. Named input even though might have _id in the
      // schema, if it comes from an "Add new row" form, it won't have row id data. That will possibly come from an insert
      // found in the workflow
      return col.id ? !columnIds.has(col.id) || !!col.isId : true;
    }
    return col.id ? !columnIds.has(col.id) : true;
  }

  private filterOutExistingNamedInputColumnsByName(
    col: DatasetColumn,
    columnNames: Set<string>,
    keepRowIdColumns: boolean,
  ): boolean {
    if (!columnNames.size) {
      return true;
    }
    if (keepRowIdColumns) {
      // BL-8201 We keep ID columns in case previous node was an insert. Named input even though might have _id in the
      // schema, if it comes from an "Add new row" form, it won't have row id data. That will possibly come from an insert
      // found in the workflow
      return col.name ? !columnNames.has(col.name) || col.name === DEFAULT_ID_COL : true;
    }
    return col.name ? !columnNames.has(col.name) : true;
  }

  /**
   * Filters out internal columns except for the ID column.
   * @param column
   * @returns
   */
  private filterOutInternal(column: DatasetColumn, keepIdColumn = true): boolean {
    if (keepIdColumn && column.isId) {
      return true;
    }
    return !column.isInternal;
  }

  /**
   * For Get workflow node, filters out selected column to avoid for example equating selected column to itself.
   * In the future this may change if we want to have a formula that takes as an input it's own output.
   */
  private filterOutSelectedColumn(col: DatasetColumn, config: IntelliConfig | null): boolean {
    // BL-8776 For triggered workflows we don't filter out currently selected column. This is because we want to
    // be able to compare previous values available from trigger nodes with current nodes in the dataset.
    if (isGetNode(config?.workflowNode)) {
      const selectedColumn = config?.column;
      return selectedColumn && config?.workflow?.workflowType !== 'Trigger'
        ? !matchColumn(col, selectedColumn)
        : true;
    }
    return true;
  }

  private filterOutUnsupportedOperatorType(
    col: DatasetColumn,
    config: IntelliConfig | null,
  ): boolean {
    // Don't allow multiple value columns for text operators
    if (!!config?.operator && this.isTextOperator(config?.operator)) {
      return !col.multipleValues;
    }
    return true;
  }

  /**
   * Fetches dataset rows for the given dataset and caches the request so that the next instance of the intellisense
   * that's initialized can reuse the fetched data (for example when multiple intellisense instances are initailized
   * in WF editor assignments).
   *
   * The cache is reset when an instance of the intellisense is destroyed. This means that when multiple instances
   * of intellisense are on one page (WF editor assignments), and one of them is hidden, the datasets will get
   * refetched until another instance is destroyed. While not optimal, this helps us when making multiple requests
   * to the same endpoint but allows us to clear cache in case dataset has changed.
   */
  private getDatasetRows(datasetIdToFetch: string): Observable<DatasetRow[]> {
    let request$ = this.datasetRowsRequests.get(datasetIdToFetch);
    if (!request$) {
      request$ = this.datasetRowsService.get({ datasetId: datasetIdToFetch, maxResults: 200 }).pipe(
        map((rows) => rows || []),
        shareReplay(1),
      );
      this.datasetRowsRequests.set(datasetIdToFetch, request$);
    }
    return request$.pipe(map((rows) => rows || []));
  }

  private getNamedInputSchemasColumnIds(
    namedInputSchemas?: Record<string, BaseSchema | undefined>,
  ): Set<string> {
    const ids = new Set<string>();
    if (!namedInputSchemas || !Object.keys(namedInputSchemas)?.length) {
      return ids;
    }
    for (const inputName in namedInputSchemas) {
      const namedInputSchema = namedInputSchemas[inputName];
      if (namedInputSchema) {
        for (const col of namedInputSchema.columns) {
          if (col.id) {
            ids.add(col.id);
          }
        }
      }
    }
    return ids;
  }

  private supportsFormulas(config: IntelliConfig | null): boolean {
    return (
      !config?.disableFormulas &&
      (config?.column?.columnType === 'Formula' || !!config?.columnTypes?.includes('Formula'))
    );
  }

  private isColumnLinkedToTable(column?: DatasetColumn | null, schema?: BlazeSchema): boolean {
    return !!column && !!schema && isLinkedColumn(column) && column.link?.tableId === schema?.id;
  }

  private isFirstNode(node: WorkflowNode | undefined | null, config: IntelliConfig): boolean {
    if (!node || !config?.workflow) {
      return false;
    }
    return config.workflow.startNodeId === node.id;
  }

  /**
   * @returns Whether the target column is a link to the Accounts table.
   */
  private isLinkToCurrentAccount(config: IntelliConfig | null): boolean {
    if (!config?.accountsSchema) {
      return false;
    }
    return config?.column?.link?.tableId === config?.accountsSchema?.id;
  }

  private isLinkToCurrentUser(config: IntelliConfig | null): boolean {
    if (!config?.usersSchema) {
      return false;
    }
    const colLinkTargetTableId = config?.column?.link?.tableId;
    return colLinkTargetTableId === config?.usersSchema?.id;
  }

  private isTagsColumn(column?: DatasetColumn | null): boolean {
    return column?.dataType === 'Tag';
  }

  private isTextOperator(operator: ComparisonOperator | undefined): boolean {
    return (
      !!operator &&
      (operator === ComparisonOperator.ContainsText ||
        operator === ComparisonOperator.HasPrefix || // starts with
        operator === ComparisonOperator.HasPrefixIgnoreCase) // starts with (ignore case)
    );
  }
}
